package org.moxultimate.topwithmox.showscrud.main

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.last
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.moxultimate.topwithmox.shows.api.ShowsRepository
import org.moxultimate.topwithmox.shows.data.ShowsResponse
import org.moxultimate.topwithmox.showscrud.api.ShowsCRUDRepository
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.utils.toTitleCase
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/**
 * ShowsCRUDViewModel
 * Manages cursor-based pagination, client-side type/search filtering,
 * and create/update/delete operations for show entries.
 *
 * @param repository Read-only data source for shows, tags, and types.
 * @param crudRepository Write repository for create, update, delete, saveTag, and saveType.
 */
class ShowsCRUDViewModel(
    private val repository: ShowsRepository,
    private val crudRepository: ShowsCRUDRepository,
) : ViewModel() {

    // ── Operation state (create / update / delete) ────────────────────────────

    private val _crudState = MutableStateFlow<ShowsCRUDState>(ShowsCRUDState.Idle)
    val crudState: StateFlow<ShowsCRUDState> = _crudState

    // ── List state ────────────────────────────────────────────────────────────

    private val _listState = MutableStateFlow<ShowsListState>(ShowsListState.Loading)
    val listState: StateFlow<ShowsListState> = _listState

    private val _isLoadingMore = MutableStateFlow(false)
    val isLoadingMore: StateFlow<Boolean> = _isLoadingMore

    private val _hasMoreInCurrentView = MutableStateFlow(true)
    val hasMoreInCurrentView: StateFlow<Boolean> = _hasMoreInCurrentView

    // ── Accumulated shows ─────────────────────────────────────────────────────

    private val accumulatedShows = mutableListOf<ShowsResponse>()
    private var lastId: Long = Long.MAX_VALUE
    private var hasMore = true

    // ── Filter state ──────────────────────────────────────────────────────────

    private val _allTypes = MutableStateFlow<List<String>>(emptyList())
    val allTypes: StateFlow<List<String>> = _allTypes

    private val _allTags = MutableStateFlow<List<String>>(emptyList())
    val allTags: StateFlow<List<String>> = _allTags

    private val _selectedTypes = MutableStateFlow<Set<String>>(emptySet())
    val selectedTypes: StateFlow<Set<String>> = _selectedTypes

    private val _searchText = MutableStateFlow("")
    val searchText: StateFlow<String> = _searchText

    // ── List loading ──────────────────────────────────────────────────────────

    /**
     * loadShows
     * Resets accumulated state and fetches the first page. Call from LaunchedEffect(Unit).
     */
    fun loadShows() {
        accumulatedShows.clear()
        lastId = Long.MAX_VALUE
        hasMore = true
        _selectedTypes.value = emptySet()
        _searchText.value = ""
        _hasMoreInCurrentView.value = true
        fetchPage(isInitialLoad = true)
    }

    /**
     * loadMoreIfNeeded
     * Called by the scroll handler when the user nears the bottom.
     * No-op when already loading or no more pages exist.
     */
    fun loadMoreIfNeeded() {
        if (hasMore && !_isLoadingMore.value) fetchPage(isInitialLoad = false)
    }

    private fun fetchPage(isInitialLoad: Boolean) {
        viewModelScope.launch {
            _isLoadingMore.value = true
            repository.getShows(lastId).collect { state ->
                _listState.value = when (state) {
                    is APIState.Loading -> {
                        if (isInitialLoad) ShowsListState.Loading else _listState.value
                    }
                    is APIState.Error -> {
                        _isLoadingMore.value = false
                        ShowsListState.Error(state.error, state.statusCode)
                    }
                    is APIState.Success -> {
                        val page = state.data
                        _isLoadingMore.value = false
                        if (page.size < 20) hasMore = false
                        accumulatedShows.addAll(page)
                        if (page.isNotEmpty()) lastId = page.last().id
                        _hasMoreInCurrentView.value = hasMore
                        buildListState()
                    }
                }
            }
        }
    }

    // ── Type / Tag loading ────────────────────────────────────────────────────

    /**
     * loadAllTypes
     * Fetches all distinct show types. Call once on page load.
     */
    fun loadAllTypes() {
        viewModelScope.launch {
            repository.getAllTypes().collect { state ->
                if (state is APIState.Success) _allTypes.value = state.data.distinct().sorted()
            }
        }
    }

    /**
     * loadAllTags
     * Fetches all show tags for search autocomplete. Call once on page load.
     */
    fun loadAllTags() {
        viewModelScope.launch {
            repository.getAllTags().collect { state ->
                if (state is APIState.Success) _allTags.value = state.data.distinct().sorted()
            }
        }
    }

    // ── Filtering ─────────────────────────────────────────────────────────────

    /**
     * toggleType
     * Adds or removes [type] from the active type filter and rebuilds state client-side.
     *
     * @param type Type string matching a value from [allTypes].
     */
    fun toggleType(type: String) {
        val current = _selectedTypes.value
        _selectedTypes.value = if (type in current) current - type else current + type
        _listState.value = buildListState()
    }

    /**
     * setSearchText
     * Updates the live search text and immediately re-filters the loaded shows list.
     *
     * @param text Text to search for in show title and description.
     */
    fun setSearchText(text: String) {
        _searchText.value = text
        _listState.value = buildListState()
    }

    /**
     * clearAllFilters
     * Resets all active type chips and search text, restoring the full shows list.
     */
    fun clearAllFilters() {
        _selectedTypes.value = emptySet()
        _searchText.value = ""
        _listState.value = buildListState()
    }

    private fun buildListState(): ShowsListState.Success {
        val types = _selectedTypes.value
        val text = _searchText.value.trim()
        val result = accumulatedShows.filter { show ->
            val matchesType = types.isEmpty() ||
                    types.any { t -> show.type?.equals(t.trim(), ignoreCase = true) == true }
            val matchesText = text.isBlank() ||
                    show.title?.contains(text, ignoreCase = true) == true ||
                    show.tags?.contains(text, ignoreCase = true) == true ||
                    show.description?.contains(text, ignoreCase = true) == true
            matchesType && matchesText
        }
        return ShowsListState.Success(shows = result, hasMore = hasMore)
    }

    // ── CRUD operations ───────────────────────────────────────────────────────

    /**
     * createShow
     * Reads image file from [entry] then creates a new show via POST /shows.
     * Also persists the type to its lookup table.
     *
     * @param entry Form data for the new show (imageFile must be non-null).
     */
    fun createShow(entry: ShowsFormEntry) {
        viewModelScope.launch {
            _crudState.value = ShowsCRUDState.Loading
            try {
                val imageBytes = readFileAsBytes(entry.imageFile!!)
                when (val result = crudRepository.createShow(
                    title       = entry.title,
                    type        = entry.type,
                    tags        = entry.tags,
                    description = entry.description,
                    videoLink   = entry.videoLink,
                    imageBytes  = imageBytes,
                ).last()) {
                    is APIState.Error -> _crudState.value = ShowsCRUDState.Error(result.error, result.statusCode)
                    is APIState.Success -> {
                        persistType(entry.type)
                        persistTags(entry.tags)
                        _crudState.value = ShowsCRUDState.Success("Show created successfully!")
                    }
                    else -> Unit
                }
            } catch (e: Throwable) {
                _crudState.value = ShowsCRUDState.Error(e.message ?: "Create failed")
            }
        }
    }

    /**
     * updateShow
     * Reads optional new image from [entry] then updates an existing show via PUT /shows/{id}.
     *
     * @param id Show ID to update.
     * @param entry Updated form data (imageFile is optional for updates).
     */
    fun updateShow(id: Long, entry: ShowsFormEntry) {
        viewModelScope.launch {
            _crudState.value = ShowsCRUDState.Loading
            try {
                val imageBytes = entry.imageFile?.let { readFileAsBytes(it) }
                when (val result = crudRepository.updateShow(
                    id          = id,
                    title       = entry.title.takeIf { it.isNotBlank() },
                    type        = entry.type.takeIf { it.isNotBlank() },
                    tags        = entry.tags,
                    description = entry.description.takeIf { it.isNotBlank() },
                    videoLink   = entry.videoLink.takeIf { it.isNotBlank() },
                    imageBytes  = imageBytes,
                ).last()) {
                    is APIState.Error -> _crudState.value = ShowsCRUDState.Error(result.error, result.statusCode)
                    is APIState.Success -> {
                        persistType(entry.type)
                        persistTags(entry.tags)
                        _crudState.value = ShowsCRUDState.Success("Show updated successfully!")
                    }
                    else -> Unit
                }
            } catch (e: Throwable) {
                _crudState.value = ShowsCRUDState.Error(e.message ?: "Update failed")
            }
        }
    }

    /**
     * deleteShow
     * Deletes a show entry and its S3 files via DELETE /shows/{id}.
     *
     * @param id Show ID to delete.
     */
    fun deleteShow(id: Long) {
        viewModelScope.launch {
            _crudState.value = ShowsCRUDState.Loading
            try {
                when (val result = crudRepository.deleteShow(id).last()) {
                    is APIState.Error -> _crudState.value = ShowsCRUDState.Error(result.error, result.statusCode)
                    else -> _crudState.value = ShowsCRUDState.Success("Show deleted successfully!")
                }
            } catch (e: Throwable) {
                _crudState.value = ShowsCRUDState.Error(e.message ?: "Delete failed")
            }
        }
    }

    /**
     * resetState
     * Resets the CRUD operation state back to Idle.
     */
    fun resetState() {
        _crudState.value = ShowsCRUDState.Idle
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    private fun persistType(type: String) {
        viewModelScope.launch {
            val titleCased = type.trim().toTitleCase()
            if (titleCased.isNotBlank()) crudRepository.saveType(titleCased).collect {}
        }
    }

    /**
     * persistTags
     * Splits the pipe-separated [tags] string, Title-Cases each entry, and fires a
     * best-effort POST /shows-tag for every unique non-blank tag.
     * Errors are silently discarded — lookup table updates never block the main flow.
     *
     * @param tags Pipe-separated tag string from the form.
     */
    private fun persistTags(tags: String) {
        tags.split("|")
            .map { it.trim().toTitleCase() }
            .filter { it.isNotBlank() }
            .distinct()
            .forEach { tag ->
                viewModelScope.launch {
                    try { crudRepository.saveTag(tag).collect {} } catch (_: Throwable) {}
                }
            }
    }

    /**
     * readFileAsBytes
     * Reads a browser File object as a ByteArray using the FileReader API.
     * Suspends until the read is complete or throws on failure.
     *
     * @param file The browser File to read.
     * @return ByteArray of the file's contents.
     */
    private suspend fun readFileAsBytes(file: File): ByteArray = suspendCancellableCoroutine { cont ->
        val reader = FileReader()
        reader.onload = {
            val buffer = (reader.result as ArrayBuffer)
            val bytes = Int8Array(buffer).unsafeCast<ByteArray>()
            cont.resume(bytes)
        }
        reader.onerror = {
            cont.resumeWithException(Exception("Failed to read file: ${file.name}"))
        }
        reader.readAsArrayBuffer(file)
    }
}

/**
 * ShowsFormEntry
 * UI-layer form state for a single show entry (add or edit).
 * [imageFile] is null when no new file has been selected.
 */
data class ShowsFormEntry(
    val imageFile: File? = null,
    val title: String = "",
    val type: String = "",
    val tags: String = "",
    val description: String = "",
    val videoLink: String = "",
) {
    /** True when all required fields and the image file are present (used for add flow). */
    val isValid: Boolean
        get() = imageFile != null && title.isNotBlank() && type.isNotBlank()

    /** True when required text fields are filled (used for update flow — image is optional). */
    val isValidForUpdate: Boolean
        get() = title.isNotBlank() && type.isNotBlank()

    /** Names of required fields that are still empty or missing — used to show validation hints. */
    val missingFields: List<String>
        get() = buildList {
            if (imageFile == null) add("Image")
            if (title.isBlank()) add("Title")
            if (type.isBlank()) add("Type")
        }
}

/**
 * toFormEntry
 * Convenience extension to pre-populate a [ShowsFormEntry] from a [ShowsResponse].
 */
fun ShowsResponse.toFormEntry() = ShowsFormEntry(
    title       = title.orEmpty(),
    type        = type.orEmpty(),
    tags        = tags.orEmpty(),
    description = description.orEmpty(),
    videoLink   = videoLink.orEmpty(),
)
